<template>
  <div class="pixel-cat-runner" :style="runnerStyle">
    <div class="pixel-cat" :style="catAnimStyle">
      <div class="pixel-cat-face">
        <div class="pixel-cat-sprite" :style="spriteStyle">
          <div class="pixel-body" :style="bodyShadowStyle"></div>
          <div class="pixel-eyes" :style="eyesShadowStyle"></div>
          <div class="pixel-cheeks" :style="cheeksShadowStyle"></div>
          <div class="pixel-nose" :style="noseShadowStyle"></div>
          <div class="pixel-tail" :style="tailShadowStyle"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * 像素风小猫跑动组件
 * 每个模块一个固定的可爱小猫，在容器内左右来回跑动（不会跑出边界）
 * 不同模块通过 catType 使用不同颜色的小猫
 */

// 像素小猫的坐标定义 (9x9 网格, x 0-8, y 0-8)
// 设计草图 (X=身体, o=眼睛, @=腮红, .=鼻子):
//   . X . . . . . X .
//   . X . . . . . X .
//   X X X X X X X X X
//   X . . . . . . . X
//   X X o o X o o X X
//   X X @ . . . @ X X
//   X X X X X X X X X
//   X X X X X X X X X
//   . . X X . X X . .
const PIXEL_BODY = [
  // 耳朵
  [1, 0], [7, 0],
  [1, 1], [7, 1],
  // 头部上缘
  [0, 2], [1, 2], [2, 2], [3, 2], [4, 2], [5, 2], [6, 2], [7, 2], [8, 2],
  // 脸颊两侧
  [0, 3], [8, 3],
  // 面部
  [0, 4], [1, 4], [2, 4], [3, 4], [4, 4], [5, 4], [6, 4], [7, 4], [8, 4],
  [0, 5], [1, 5], [2, 5], [3, 5], [4, 5], [5, 5], [6, 5], [7, 5], [8, 5],
  [0, 6], [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 6], [7, 6], [8, 6],
  // 下巴
  [0, 7], [1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7], [7, 7], [8, 7],
  // 嘴角
  [2, 8], [3, 8], [5, 8], [6, 8],
];

// 眼睛（亮色，各2像素宽）
const PIXEL_EYES = [
  [2, 4], [3, 4], [5, 4], [6, 4],
];

// 腮红（粉色）
const PIXEL_CHEEKS = [
  [1, 5], [7, 5],
];

// 鼻子
const PIXEL_NOSE = [
  [4, 5],
];

// 尾巴（在身体同侧，跑动时摆动）
const PIXEL_TAIL = [
  [8, 6], [8, 7],
];

// 不同猫类型的配色方案
const CAT_THEMES = [
  { body: '#F5A623', eyes: '#FFFFFF', pupil: '#6B3E1E', cheek: '#FF8A9A', nose: '#C96A2B', name: '橘猫' },
  { body: '#A8B6D6', eyes: '#FFFFFF', pupil: '#3A4A6B', cheek: '#FFB3C1', nose: '#7A8BB0', name: '灰猫' },
  { body: '#F0B6CE', eyes: '#FFFFFF', pupil: '#8A4A66', cheek: '#FF9FB0', nose: '#C97A97', name: '粉猫' },
  { body: '#4A4A52', eyes: '#FFD700', pupil: '#2A2A30', cheek: '#FF9FB0', nose: '#6A6A72', name: '黑猫' },
];

export default {
  name: 'PixelCatRunner',
  props: {
    speed: {
      type: Number,
      default: 1,
    },
    height: {
      type: Number,
      default: 32,
    },
    catType: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    theme() {
      return CAT_THEMES[this.catType % CAT_THEMES.length];
    },
    pixelSize() {
      // 根据容器高度计算像素点大小，让小猫刚好适配高度
      return Math.max(2, Math.floor(this.height / 9));
    },
    spriteWidth() {
      return 9 * this.pixelSize;
    },
    spriteHeight() {
      return 9 * this.pixelSize;
    },
    runnerStyle() {
      return {
        height: this.height + 'px',
        '--cat-w': this.spriteWidth + 'px',
      };
    },
    _shadowSet(points) {
      const px = this.pixelSize;
      return points.map(([x, y]) => `${x * px}px ${y * px}px 0 0 `);
    },
    bodyShadowStyle() {
      const px = this.pixelSize;
      const shadows = PIXEL_BODY.map(([x, y]) => `${x * px}px ${y * px}px 0 0 ${this.theme.body}`);
      return {
        width: px + 'px',
        height: px + 'px',
        boxShadow: shadows.join(', '),
      };
    },
    eyesShadowStyle() {
      const px = this.pixelSize;
      const shadows = PIXEL_EYES.map(([x, y]) => `${x * px}px ${y * px}px 0 0 ${this.theme.eyes}`);
      return {
        width: px + 'px',
        height: px + 'px',
        boxShadow: shadows.join(', '),
      };
    },
    cheeksShadowStyle() {
      const px = this.pixelSize;
      const shadows = PIXEL_CHEEKS.map(([x, y]) => `${x * px}px ${y * px}px 0 0 ${this.theme.cheek}`);
      return {
        width: px + 'px',
        height: px + 'px',
        boxShadow: shadows.join(', '),
      };
    },
    noseShadowStyle() {
      const px = this.pixelSize;
      const shadows = PIXEL_NOSE.map(([x, y]) => `${x * px}px ${y * px}px 0 0 ${this.theme.nose}`);
      return {
        width: px + 'px',
        height: px + 'px',
        boxShadow: shadows.join(', '),
      };
    },
    tailShadowStyle() {
      const px = this.pixelSize;
      const shadows = PIXEL_TAIL.map(([x, y]) => `${x * px}px ${y * px}px 0 0 ${this.theme.body}`);
      return {
        width: px + 'px',
        height: px + 'px',
        boxShadow: shadows.join(', '),
      };
    },
    spriteStyle() {
      return {
        width: this.spriteWidth + 'px',
        height: this.spriteHeight + 'px',
      };
    },
    catAnimStyle() {
      // 放慢：速度越大跑得越慢
      return {
        animationDuration: (10 / this.speed) + 's',
      };
    },
  },
};
</script>

<style scoped>
.pixel-cat-runner {
  position: relative;
  width: 100%;
  overflow: hidden;
  pointer-events: none;
}

.pixel-cat {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  animation: catRun linear infinite;
  will-change: left;
}

.pixel-cat-face {
  animation: catFlip linear infinite;
}

.pixel-cat-sprite {
  position: relative;
  animation: catBounce 0.4s ease-in-out infinite alternate;
}

.pixel-body,
.pixel-eyes,
.pixel-cheeks,
.pixel-nose,
.pixel-tail {
  position: absolute;
  top: 0;
  left: 0;
}

.pixel-eyes,
.pixel-cheeks,
.pixel-nose {
  z-index: 1;
}

.pixel-tail {
  z-index: 0;
}

/* 在容器内左右来回跑动，始终不越界 */
@keyframes catRun {
  0% {
    left: 0;
  }
  50% {
    left: calc(100% - var(--cat-w));
  }
  100% {
    left: 0;
  }
}

/* 往右跑时朝右，往左跑时朝左 */
@keyframes catFlip {
  0%, 49% {
    transform: scaleX(-1);
  }
  50%, 100% {
    transform: scaleX(1);
  }
}

/* 上下弹跳 */
@keyframes catBounce {
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-3px);
  }
}
</style>